import React, {useState} from 'react'
import {Button} from '../../../../../core/shared/Button'
import {getPriceForCountry} from '../../../utils/getPriceForCountry'
import {
  AddToOrderRow,
  PanelContainer,
  EntitlementsTitle,
} from '../ProductPlanSelector.styles'

const HVAC_CODES = [
  'maxhvac-5-thryv-m2m',
  'maxroof-5-thryv-m2m',
  'maxelect-5-thryv-m2m',
  'maxplumb-5-thryv-m2m',
]

// All vertical clientsvcs variants — used to strip any stale code from
// the cart when a new HVAC product is added (via the excludes field).
// The correct MC-tier variant is then resolved by Rule 3d in
// productSelectionRules.js resolveItemsAfterAdd, which has full cart
// context including alreadyIncluded account defaults.
const VERTICAL_CLIENTSVCS_CODES = [
  'vert_clientsvcs-5-thryv-ot',
  'vert_clientsvcs-5-thryv-m2m',
  'vert_mc_clientsvcs-5-thryv-m2m',
  'vert_mc_clientsvcs-5-thryv-ot',
  'vert_mc_web_clientsvcs-5-thryv-m2m',
  'vert_mc_web_clientsvcs-5-thryv-ot',
]

// Fees waived when an HVAC product is added — mirrors legacy options.js:2735.
const HVAC_WAIVED_FEE_CODES = ['thryvprfee', 'thryvdesg2']

// Maps the rep's payment-type choice to the base (no-MC) vertical code.
// Rule 3d in productSelectionRules.js resolveItemsAfterAdd will upgrade this
// to the correct MC-tier variant based on cart state and account defaults.
const BASE_VERTICAL_BY_PAYMENT = {
  oneTime: 'vert_clientsvcs-5-thryv-ot',
  '6Monthly': 'vert_clientsvcs-5-thryv-m2m',
}

export function isHvacPlan(code) {
  return HVAC_CODES.includes(code)
}

export function HvacAddonPanel({
  plan,
  selectedCountry,
  itemInOrder,
  groupPlanCodes,
  groupId,
  onAddProduct,
  onClose,
}) {
  const [paymentChoice, setPaymentChoice] = useState(null)

  const handleAddToOrder = () => {
    if (!paymentChoice) return

    // Pass the base (no-MC) vertical code for the chosen payment type.
    // Rule 3d in productSelectionRules.js resolveItemsAfterAdd will
    // immediately upgrade it to the correct MC-tier variant based on full
    // cart state (MC Plus/Pro presence, ent-5-website entitlement) and
    // account defaults (isMCPlusProInDefault via alreadyIncluded items),
    // matching the legacy options.js:2592-2731 decision tree exactly.
    const baseVerticalCode = BASE_VERTICAL_BY_PAYMENT[paymentChoice]
    const isOneTime = paymentChoice === 'oneTime'

    const product = {
      productId: plan.code,
      name: plan.name,
      price: getPriceForCountry(plan.currencies, selectedCountry),
      productType: 'APP',
      billingFrequency: 'monthly',
      quantity: 1,
      configuredOptions: {},
      // Strip all existing vertical variants and waived fees so Rule 3d
      // starts with a clean slate — mirrors legacy options.js:2597-2607 and
      // options.js:2735 (waiveHVACFees).
      excludes: [
        ...(plan.excludes ?? []),
        ...VERTICAL_CLIENTSVCS_CODES,
        ...HVAC_WAIVED_FEE_CODES,
      ],
      autoAddCenters: plan.autoAddCenters ?? [],
      autoAddOneTimeFees: [],
      // Seed the cart with the base payment-type-encoded vertical; Rule 3d
      // will swap it for the correct MC-tier code before the cart is stored.
      autoAddItems: [
        {
          code: baseVerticalCode,
          name: 'Vertical Client Services',
          productType: isOneTime ? 'ONE_TIME_FEE' : 'APP',
          billingFrequency: isOneTime ? null : 'monthly',
        },
      ],
      disableApps: plan.disableApps ?? [],
      disableCenters: plan.disableCenters ?? [],
      maxQty: plan.maxQty ?? null,
      parentProduct: plan.parentProduct,
    }

    onAddProduct(product, groupPlanCodes, groupId)
    onClose()
  }

  return (
    <PanelContainer>
      <EntitlementsTitle>
        How would you like to pay your vertical service fees?
      </EntitlementsTitle>
      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
          }}
        >
          <input
            type="radio"
            name={`hvac-payment-${plan.code}`}
            value="oneTime"
            checked={paymentChoice === 'oneTime'}
            onChange={() => setPaymentChoice('oneTime')}
          />
          All at once
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
          }}
        >
          <input
            type="radio"
            name={`hvac-payment-${plan.code}`}
            value="6Monthly"
            checked={paymentChoice === '6Monthly'}
            onChange={() => setPaymentChoice('6Monthly')}
          />
          Over 6 invoices
        </label>
      </div>
      <AddToOrderRow>
        <Button
          type="button"
          variant="action-orange"
          size="sm"
          disabled={!paymentChoice}
          onClick={handleAddToOrder}
        >
          {itemInOrder ? 'Update Order' : 'Add to Order'}
        </Button>
      </AddToOrderRow>
    </PanelContainer>
  )
}
